import { useState, useRef, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { useUserStats } from "../lib/UserStatsContext";
import { useSignInPrompt } from "./AuthGate";
import { useBadgeToast } from "./BadgeToast";
import { useImageUpload } from "../lib/useImageUpload";
import { AIContent } from "./GIBadge";
import { sendMessage } from "../lib/gemini";
import type { ChatMessage } from "../lib/gemini";
import { recordQuery, recordPhotoAnalysis, recordFoodSearch, getUserStats, checkAndAwardBadges, checkUsageLimit, recordFeatureUsage } from "../lib/userStats";
import { extractFoodMentions } from "../lib/recommendations";
import { trackChatQuery, trackChatResponse, trackFoodSearch as trackFoodSearchEvent, trackError } from "../lib/analytics";
import UsageLimitModal from "./UsageLimitModal";
import UsageIndicator from "./UsageIndicator";

const ANON_QUERY_KEY = "glycemic_anon_queries";
const ANON_QUERY_LIMIT = 3;

function getAnonQueryCount(): number {
  try {
    return Number(localStorage.getItem(ANON_QUERY_KEY) || "0");
  } catch { return 0; }
}

function incrementAnonQueryCount(): void {
  try {
    localStorage.setItem(ANON_QUERY_KEY, String(getAnonQueryCount() + 1));
  } catch { /* ignore */ }
}

const SUGGESTED_QUESTIONS: string[] = [
  "What's the GI of rice?",
  "Best low-GI snacks?",
  "Is fruit bad for blood sugar?",
  "How does fiber affect GI?",
];

export default function Chat() {
  const { user, signOut } = useAuth();
  const { dietaryPrefs } = useUserStats();
  const { promptSignIn } = useSignInPrompt();
  const { showBadge } = useBadgeToast();
  const { preview: selectedImage, imageData, fileInputRef, handleImageSelect, removeImage } = useImageUpload();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ currentUsage: number; isPremiumOnly: boolean }>({ currentUsage: 0, isPremiumOnly: false });
  const [usageRefreshKey, setUsageRefreshKey] = useState(0);
  const [anonQueriesUsed, setAnonQueriesUsed] = useState(getAnonQueryCount);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollH = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(Math.max(scrollH, 38), 200)}px`;
    }
  }, [input]);

  const handleSend = async () => {
    if (!input.trim() && !imageData) return;

    // Anonymous user: check local query limit
    if (!user) {
      if (anonQueriesUsed >= ANON_QUERY_LIMIT) {
        promptSignIn("Sign in for unlimited searches — you've used your 3 free queries");
        return;
      }
    }

    // Signed-in user: check server usage limit
    if (user) {
      const usageCheck = await checkUsageLimit(user.uid, "chat");
      if (!usageCheck.allowed) {
        setLimitInfo({ currentUsage: usageCheck.currentUsage, isPremiumOnly: usageCheck.isPremiumOnly });
        setShowLimitModal(true);
        return;
      }
    }

    const messageContent = input || "What's in this food?";

    const userMessage: ChatMessage = {
      role: "user",
      content: messageContent,
      image: selectedImage || undefined,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    const startTime = Date.now();

    try {
      const response = await sendMessage(messages, messageContent, imageData || undefined, user ? dietaryPrefs : undefined);
      setMessages((prev) => [...prev, { role: "model", content: response }]);

      const responseTime = Date.now() - startTime;

      if (user) {
        await recordFeatureUsage(user.uid, "chat");
        setUsageRefreshKey((k) => k + 1);

        trackChatQuery({
          queryLength: messageContent.length,
          activityContext: "general",
          hasImage: imageData !== null,
          responseTime,
        });

        const giBadgeMatches = response.match(/\{\{gi:\d+:gl:\d+:\w+\}\}/g) || [];
        trackChatResponse({
          responseLength: response.length,
          hadGiBadges: giBadgeMatches.length > 0,
          badgeCount: giBadgeMatches.length,
        });

        const hadPhoto = imageData !== null;
        recordQuery(user.uid).then(async () => {
          if (hadPhoto) {
            await recordPhotoAnalysis(user.uid);
          }
          const foodMentions = extractFoodMentions(messageContent);
          if (foodMentions.length > 0) {
            await recordFoodSearch(user.uid, foodMentions);
            foodMentions.forEach((food) => trackFoodSearchEvent(food, "general"));
          }
          const stats = await getUserStats(user.uid);
          const newBadges = await checkAndAwardBadges(user.uid, stats);
          newBadges.forEach((badge) => showBadge(badge));
        });
      } else {
        incrementAnonQueryCount();
        setAnonQueriesUsed(getAnonQueryCount());
        trackChatQuery({
          queryLength: messageContent.length,
          activityContext: "general",
          hasImage: imageData !== null,
          responseTime,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";

      if (errorMessage.includes("authenticated") || errorMessage.includes("sign in")) {
        promptSignIn("Sign in to continue searching");
      } else {
        trackError({
          errorType: "chat_error",
          errorMessage,
          context: "chat_send",
        });
        setMessages((prev) => [
          ...prev,
          { role: "model", content: "Sorry, I had trouble processing that. Please try again." },
        ]);
      }
    } finally {
      setIsLoading(false);
      removeImage();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderInputArea = () => (
    <>
      {selectedImage && (
        <div className="mb-3 relative inline-block">
          <img src={selectedImage} alt="Selected" className="h-20 rounded-md border border-border object-cover" />
          <button
            onClick={removeImage}
            className="absolute -top-2 -right-2 w-6 h-6 bg-bg border border-border text-muted hover:text-text rounded-md text-xs flex items-center justify-center transition-colors"
          >
            X
          </button>
        </div>
      )}
      <div className="flex items-center border border-border rounded-lg bg-bg has-[:focus]:border-accent has-[:focus]:ring-1 has-[:focus]:ring-accent/20 transition-colors">
        <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex-shrink-0 px-3 text-muted hover:text-text transition-colors"
          title="Upload photo"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" />
          </svg>
        </button>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about any food..."
          rows={1}
          className="flex-1 min-w-0 resize-none bg-transparent py-2.5 text-sm text-text placeholder:text-muted outline-none border-none focus:ring-0"
        />
        <button
          onClick={handleSend}
          disabled={isLoading || (!input.trim() && !imageData)}
          className="flex-shrink-0 bg-accent text-white rounded-md px-4 py-1.5 mr-1.5 font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 active:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          Send
        </button>
      </div>
    </>
  );

  return (
    <div className="flex flex-col h-[100dvh] bg-bg">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-border bg-bg">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-text">GlycemicAI</h1>
              <p className="text-sm text-muted">Your smart food companion</p>
            </div>
            <div className="flex items-center gap-3">
              {messages.length > 0 && (
                <button
                  onClick={() => setMessages([])}
                  className="text-muted text-sm font-[family-name:--font-ui] hover:text-text transition-colors"
                >
                  New search
                </button>
              )}
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 p-1 rounded-md hover:bg-[#f0ece6] transition-colors"
                  >
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.displayName || "User"} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#f0ece6] flex items-center justify-center text-text text-sm font-medium font-[family-name:--font-ui]">
                        {user.displayName?.[0] || "U"}
                      </div>
                    )}
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-bg rounded-md border border-border py-1 z-50">
                      <div className="px-4 py-2 border-b border-border">
                        <p className="text-sm font-medium text-text truncate">{user.displayName}</p>
                        <p className="text-xs text-muted truncate">{user.email}</p>
                      </div>
                      <button
                        onClick={() => signOut()}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-[#f0ece6]"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => promptSignIn("Sign in for unlimited searches and personalized results")}
                  className="text-accent text-sm font-[family-name:--font-ui] font-medium hover:underline"
                >
                  Sign in
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {messages.length === 0 ? (
            <div className="flex flex-col pt-8">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-text mb-2">Search foods</h2>
                <p className="text-sm text-muted max-w-md mx-auto">
                  Ask about any food or upload a photo. Get GI, GL, and context.
                </p>
              </div>

              <div className="w-full mb-8">
                {renderInputArea()}
              </div>

              <div className="text-center">
                <p className="section-label mb-4">Try asking</p>
                <div className="flex flex-col items-center gap-3">
                  {SUGGESTED_QUESTIONS.map((question, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setInput(question);
                        setTimeout(handleSend, 100);
                      }}
                      className="text-accent text-sm font-[family-name:--font-ui] hover:underline cursor-pointer"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col items-center mt-8">
                {user ? (
                  <UsageIndicator feature="chat" compact onLimitReached={() => setShowLimitModal(true)} refreshKey={usageRefreshKey} />
                ) : (
                  <p className="text-xs text-muted font-[family-name:--font-ui]">
                    {ANON_QUERY_LIMIT - anonQueriesUsed} free {ANON_QUERY_LIMIT - anonQueriesUsed === 1 ? "query" : "queries"} remaining
                  </p>
                )}
                <p className="text-[11px] text-muted font-[family-name:--font-ui] mt-2">
                  GI values are estimates. Consult a healthcare provider for medical advice.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((message, i) => (
                <div key={i} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 ${
                      message.role === "user"
                        ? "bg-[#f0ece6] rounded-lg ml-auto"
                        : "mr-auto"
                    }`}
                  >
                    {message.image && (
                      <img src={message.image} alt="Uploaded food" className="rounded-md mb-2 max-h-48 object-cover" />
                    )}
                    {message.role === "user" ? (
                      <div className="text-sm leading-relaxed text-text">{message.content}</div>
                    ) : (
                      <div className="text-sm leading-relaxed">
                        <AIContent content={message.content} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="px-4 py-3">
                    <div className="spinner"></div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </main>

      {/* Input - only in footer during conversation */}
      {messages.length > 0 && (
        <footer className="flex-shrink-0 border-t border-border bg-bg pb-[env(safe-area-inset-bottom)]">
          <div className="max-w-3xl mx-auto px-4 py-3">
            {renderInputArea()}
            <div className="flex items-center justify-center mt-2">
              {user ? (
                <UsageIndicator feature="chat" compact onLimitReached={() => setShowLimitModal(true)} refreshKey={usageRefreshKey} />
              ) : (
                <p className="text-xs text-muted font-[family-name:--font-ui]">
                  {anonQueriesUsed >= ANON_QUERY_LIMIT
                    ? <button onClick={() => promptSignIn("Sign in for unlimited searches")} className="text-accent hover:underline">Sign in to continue</button>
                    : `${ANON_QUERY_LIMIT - anonQueriesUsed} free ${ANON_QUERY_LIMIT - anonQueriesUsed === 1 ? "query" : "queries"} remaining`
                  }
                </p>
              )}
            </div>
          </div>
        </footer>
      )}

      {/* Usage Limit Modal */}
      <UsageLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        feature="chat"
        currentUsage={limitInfo.currentUsage}
        isPremiumOnly={limitInfo.isPremiumOnly}
      />
    </div>
  );
}
